import React from 'react';
import { Button, Modal } from 'antd';
import { SquaresFourIcon, TreeEvergreenIcon, CubeIcon, CheckCircleIcon, PlusIcon } from '@phosphor-icons/react';
import { useViewerStore } from '../../../../../store/store';

export interface DashboardMaxPanelsTreeCuttingModalProps {
    theme: string;
    open: boolean;
    onClose: () => void;
    best5MaxPanelsTreeCuttingDesigns: any[];
    treeCount: number;
    applyingScenarioIndex: number | null | undefined;
    handleUseThisLayout: (scenarioIndex: number) => void;
    setIsDashboardOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    selectedRoofCombinationKey: string | null | undefined;
    isTreeCutScenarioSavedForLayout: (layoutKey: string, index: number) => boolean;
    aiModuleFillingScenarios: any[] | null | undefined;
    handleSaveTreeCutScenarioWithLayoutKey: (layoutKey: string, scenario: any, index: number) => void;
}

export function DashboardMaxPanelsTreeCuttingModal({
    theme,
    open,
    onClose,
    best5MaxPanelsTreeCuttingDesigns,
    treeCount,
    applyingScenarioIndex,
    handleUseThisLayout,
    setIsDashboardOpen,
    selectedRoofCombinationKey,
    isTreeCutScenarioSavedForLayout,
    aiModuleFillingScenarios,
    handleSaveTreeCutScenarioWithLayoutKey,
}: DashboardMaxPanelsTreeCuttingModalProps) {
    const layoutKey = selectedRoofCombinationKey ?? 'default';

    return (
        <Modal
            title={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <SquaresFourIcon size={20} weight="duotone" /> <TreeEvergreenIcon size={18} weight="duotone" /> Max
                    Panels + Tree cutting
                </span>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width={960}
            className={`editor-ai-tree-cutting-modal editor-ai-maxpanels-modal obd-theme-${theme}`}
            rootClassName={`obd-form-controls-modal-root obd-theme-${theme}`}
            destroyOnHidden
        >
            <div
                className="editor-run-ai-result-card editor-ai-tree-cutting-modal__body"
                style={{ overflow: 'auto', maxHeight: '70vh', padding: '4px 0' }}
            >
                <div className="editor-ai-tree-cutting-modal__header">
                    <p
                        className="editor-ai-tree-cutting-modal__intro"
                        style={{
                            fontSize: 12,
                            color: theme === 'dark' ? 'rgba(203,213,225,0.95)' : '#475569',
                            margin: 0,
                        }}
                    >
                        Top 5 designs combining max panels layout with tree-cutting options, ranked by production and
                        cost-effectiveness.
                    </p>
                </div>
                {best5MaxPanelsTreeCuttingDesigns.length === 0 ? (
                    <p style={{ color: 'rgba(156,163,175,0.9)', fontSize: 13 }}>
                        Production uses shade weights and tree multipliers from the current design. Close this modal and
                        click the &quot;Max Panels + Tree cutting&quot; card again to run production (or use Run AI in the
                        layout section first).
                    </p>
                ) : (
                    <div className="editor-ai-tree-cutting-cards editor-ai-maxpanels-cards">
                        {best5MaxPanelsTreeCuttingDesigns.map((d: any, i: number) => (
                            <div
                                key={d.scenarioIndex}
                                className={`editor-ai-scenario-card editor-ai-scenario-card--square ${d.isBase ? 'editor-ai-scenario-card--base' : ''}`}
                            >
                                <div className="editor-ai-scenario-card__title">
                                    <span className="editor-ai-maxpanels-rank">#{i + 1}</span>
                                    <span className="editor-ai-scenario-card__title-text">{d.treeLabel}</span>
                                </div>
                                <div className="editor-ai-scenario-card__meta">
                                    <span>
                                        Modules: <strong>{d.modules ?? '—'}</strong>
                                    </span>
                                    <span>
                                        Roofs: <strong>{d.roofs ?? '—'}</strong>
                                    </span>
                                    <span>
                                        Prod: <strong>{Math.round(d.actualWattage).toLocaleString()} W</strong>
                                    </span>
                                    <span>
                                        System: <strong>{(d.actualWattage / 1000).toFixed(2)} kW</strong>
                                    </span>
                                    <span>
                                        Trees: <strong>{treeCount}</strong>
                                    </span>
                                </div>
                                <div className="editor-ai-scenario-card__labels">
                                    {d.cutCost > 0 && (
                                        <span>
                                            Cut: <strong>${Math.round(d.cutCost).toLocaleString()}</strong>
                                        </span>
                                    )}
                                    {d.gain > 0 && (
                                        <span className="editor-ai-scenario-card__gain">
                                            +{Math.round(d.gain).toLocaleString()} W vs base
                                        </span>
                                    )}
                                </div>
                                <div className="editor-ai-scenario-card__footer" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <Button
                                        type="default"
                                        size="small"
                                        className="editor-ai-btn-use-scenario"
                                        loading={applyingScenarioIndex === d.scenarioIndex}
                                        onClick={() => {
                                            useViewerStore.setState({ isAiGeneratedDesign: true });
                                            handleUseThisLayout(d.scenarioIndex);
                                            onClose();
                                            setIsDashboardOpen(false);
                                        }}
                                        icon={<CubeIcon size={14} weight="duotone" />}
                                        title="Open in 3D viewer"
                                    />
                                    <Button
                                        size="small"
                                        type={
                                            isTreeCutScenarioSavedForLayout(layoutKey, d.scenarioIndex)
                                                ? 'default'
                                                : 'primary'
                                        }
                                        icon={
                                            isTreeCutScenarioSavedForLayout(layoutKey, d.scenarioIndex) ? (
                                                <CheckCircleIcon size={14} weight="fill" />
                                            ) : (
                                                <PlusIcon size={14} weight="bold" />
                                            )
                                        }
                                        disabled={isTreeCutScenarioSavedForLayout(layoutKey, d.scenarioIndex)}
                                        onClick={() => {
                                            const scenario = aiModuleFillingScenarios?.[d.scenarioIndex];
                                            if (scenario)
                                                handleSaveTreeCutScenarioWithLayoutKey(
                                                    layoutKey,
                                                    scenario,
                                                    d.scenarioIndex,
                                                );
                                        }}
                                        title={
                                            isTreeCutScenarioSavedForLayout(layoutKey, d.scenarioIndex)
                                                ? 'Saved'
                                                : 'Save design'
                                        }
                                        style={{ borderRadius: 999, minWidth: 32 }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
}
